// =============================================================================
// Portal Items — Unified data model for the 5-layout Tasks & Files tab
// =============================================================================
// Normalizes ClientTask, ClientFile, ClientCommunication into a single typed
// array. Each layout variant renders the same PortalItem[] differently.
//
// Type detection:
//   task     → from ClientTask
//   file     → from ClientFile (default)
//   invoice  → from ClientFile when filename contains "inv" (case-insensitive)
//   meeting  → from ClientCommunication
//
// Status computation:
//   Tasks:   overdue (past due + not done), due_soon (within 3 days), maps status
//   Files:   completed (shared = done), invoices get pending
//   Comms:   no_date (info items)
// =============================================================================

import type { ClientTask, ClientFile, ClientCommunication } from "../types";
import { getDateKeyInTZ, localDateKey } from "./timezone";

// ---------------------------------------------------------------------------
// Due dates — the ONE engine for "is this task overdue / due when"
// ---------------------------------------------------------------------------
// client_tasks.due_date is a DATE-ONLY value ("2026-10-06"), not an instant.
// Never `new Date(due_date)`: that is UTC midnight, so a task due Oct 6 read
// "overdue" from 08:00 SGT on Oct 6, and from ~5pm Oct 5 for a US viewer.
// Compare calendar-day KEYS instead. "Today" is the ORG's calendar day
// (Mako's Mar 26 2026 ruling: org-business date-only fields use org tz);
// with no org tz known, fall back to the viewer's calendar day.

const DATE_KEY_RE = /^(\d{4})-(\d{2})-(\d{2})/;

/** "2026-10-06" (or "2026-10-06T…") → "2026-10-06"; anything else → null. */
export function dueDateKey(due: string | null | undefined): string | null {
  const m = due ? DATE_KEY_RE.exec(due) : null;
  return m ? `${m[1]}-${m[2]}-${m[3]}` : null;
}

/** Today's calendar-day key in the org timezone (viewer's day if tz unknown). */
export function orgTodayKey(tz: string | null | undefined): string {
  const now = new Date();
  if (!tz) return localDateKey(now);
  try {
    return getDateKeyInTZ(now.toISOString(), tz);
  } catch {
    // Malformed tz string (Intl throws RangeError): fall back to the viewer's day, never crash render.
    return localDateKey(now);
  }
}

/**
 * Month-to-date picker range for an org "today" key (YYYY-MM-DD in org tz).
 * Picker Dates are browser-local Y/M/D (see toOrgStartISO), so build them that way.
 * The goal bar counts THIS range, never the date picker (Mako 2026-10-07, CO13 = A).
 */
export function monthToDateRange(todayKey: string): { start: Date; end: Date } {
  const [y, m, d] = todayKey.split("-").map(Number);
  return { start: new Date(y, m - 1, 1), end: new Date(y, m - 1, d) };
}

/** Whole days from key `from` to key `to` (to − from). Pure calendar math, no tz. */
export function dayDiff(from: string, to: string): number {
  const [fy, fm, fd] = from.split("-").map(Number);
  const [ty, tm, td] = to.split("-").map(Number);
  return Math.round((Date.UTC(ty, tm - 1, td) - Date.UTC(fy, fm - 1, fd)) / 86_400_000);
}

/** Days past due: positive = overdue by N, 0 = due today, negative = future, null = no date. */
export function daysPastDue(due: string | null | undefined, todayKey: string): number | null {
  const key = dueDateKey(due);
  return key ? dayDiff(key, todayKey) : null;
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** "Oct 6" straight from the date key — no Date object, so no day shift in any tz. */
export function formatDueKeyShort(key: string): string {
  const [, m, d] = key.split("-").map(Number);
  return `${MONTHS[m - 1]} ${d}`;
}

export type PortalItemType = "task" | "file" | "meeting" | "invoice";

export type PortalItemStatus =
  | "pending"
  | "in_progress"
  | "completed"
  | "overdue"
  | "due_soon"
  | "no_date";

export interface PortalItem {
  id: string;
  type: PortalItemType;
  title: string;
  description?: string;
  status: PortalItemStatus;
  date?: Date;
  dateLabel: string;
  link?: string;
  _original: ClientTask | ClientFile | ClientCommunication;
  _sourceType: "task" | "file" | "communication";
  // Apr 6 enrichment fields (tasks only; undefined for files/comms)
  priority?: ClientTask["priority"];
  assignee_id?: string | null;
  subtaskCount?: number; // count of direct children (parent_task_id = this.id)
  subtasksDone?: number; // count of children with status = 'completed'
  commentCount?: number; // from PostgREST nested count on client_task_comments
  section_id?: string | null; // Kanban section FK (Apr 24)
  dueDateString?: string | null; // Raw YYYY-MM-DD string from DB — avoids UTC-parse trap on Date construction
  isDraft?: boolean; // G2: mute flag — draft tasks show reduced opacity + "Draft" pill
  // Days past due in the ORG's calendar (positive = overdue, 0 = today, negative =
  // future, null = no date or completed). Precomputed once so every view agrees.
  daysOverdue?: number | null;
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

function computeTaskStatus(task: ClientTask, todayKey: string): PortalItemStatus {
  if (task.status === "completed") return "completed";
  // Cancelled tasks are closed: never overdue, never "due soon".
  if (task.status === "cancelled") return "pending";

  const over = daysPastDue(task.due_date, todayKey);
  if (over !== null) {
    if (over > 0) return "overdue"; // due today is NOT overdue
    if (over >= -3) return "due_soon"; // today .. 3 days out
  }

  if (task.status === "in_progress") return "in_progress";
  return "pending";
}

// Whole-word "inv"/"invoice" only: "INV-0042.pdf", "Invoice March.pdf",
// "march_invoice.pdf" — not "Inventory.pdf", "Investor deck", "Invite".
function isInvoiceFile(fileName: string): boolean {
  return /invoice/i.test(fileName) || /(^|[^a-z])inv([^a-z]|$)/i.test(fileName);
}

function formatDueLabel(key: string, todayKey: string): string {
  return key === todayKey ? "Due today" : `Due ${formatDueKeyShort(key)}`;
}

function formatDateLabel(date: Date | undefined, prefix?: string): string {
  if (!date) return "";

  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const target = new Date(date.getFullYear(), date.getMonth(), date.getDate());

  if (target.getTime() === today.getTime()) {
    return prefix ? `${prefix} today` : "Today";
  }

  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  const label = `${months[date.getMonth()]} ${date.getDate()}`;
  return prefix ? `${prefix} ${label}` : label;
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Converts raw portal data arrays into a unified, sorted PortalItem[].
 * Sort order: overdue → due_soon → in_progress → pending → no_date → completed.
 * Within same status, earlier dates sort first.
 */
export function normalizePortalItems(
  tasks: ClientTask[],
  files: ClientFile[],
  communications: ClientCommunication[],
  orgTimezone?: string | null
): PortalItem[] {
  const items: PortalItem[] = [];
  const todayKey = orgTodayKey(orgTimezone);

  // Pre-compute subtask counts keyed by parent_task_id
  const subtaskCountMap = new Map<string, number>();
  const subtasksDoneMap = new Map<string, number>();
  for (const task of tasks) {
    if (task.parent_task_id) {
      subtaskCountMap.set(task.parent_task_id, (subtaskCountMap.get(task.parent_task_id) ?? 0) + 1);
      const done = task.status === "completed";
      if (done) {
        subtasksDoneMap.set(
          task.parent_task_id,
          (subtasksDoneMap.get(task.parent_task_id) ?? 0) + 1
        );
      }
    }
  }

  // Tasks → PortalItem
  for (const task of tasks) {
    const dueKey = dueDateKey(task.due_date);
    // `date` is only a sort key here; every due-date DISPLAY goes through dueKey.
    const date = dueKey ? new Date(`${dueKey}T00:00:00Z`) : undefined;
    items.push({
      id: task.id,
      type: "task",
      title: task.title,
      description: task.description,
      status: computeTaskStatus(task, todayKey),
      date,
      dateLabel: dueKey ? formatDueLabel(dueKey, todayKey) : "No date",
      daysOverdue: task.status === "completed" ? null : daysPastDue(task.due_date, todayKey),
      _original: task,
      _sourceType: "task",
      priority: task.priority,
      assignee_id: task.assigned_to ?? null,
      subtaskCount: subtaskCountMap.get(task.id) ?? 0,
      subtasksDone: subtasksDoneMap.get(task.id) ?? 0,
      commentCount: task.comment_count ?? 0,
      section_id: task.section_id ?? null,
      dueDateString: task.due_date ?? null,
      isDraft: task.is_draft ?? false,
    });
  }

  // Files → PortalItem (invoice detection by filename)
  for (const file of files) {
    const date = file.shared_at ? new Date(file.shared_at) : undefined;
    const isInv = isInvoiceFile(file.file_name);
    items.push({
      id: file.id,
      type: isInv ? "invoice" : "file",
      title: file.file_name,
      description: file.profiles?.full_name ? `Shared by ${file.profiles.full_name}` : undefined,
      status: isInv ? "pending" : "completed",
      date,
      dateLabel: date ? formatDateLabel(date, "Shared") : "",
      link: file.s3_url,
      _original: file,
      _sourceType: "file",
    });
  }

  // Communications → PortalItem (always type "meeting")
  for (const comm of communications) {
    const date = comm.created_at ? new Date(comm.created_at) : undefined;
    items.push({
      id: comm.id,
      type: "meeting",
      title: comm.notes,
      description: comm.communication_type,
      status: "no_date",
      date,
      dateLabel: date ? formatDateLabel(date) : "",
      link: comm.link,
      _original: comm,
      _sourceType: "communication",
    });
  }

  // Sort: urgency-first, then by date within same status
  const statusOrder: Record<PortalItemStatus, number> = {
    overdue: 0,
    due_soon: 1,
    in_progress: 2,
    pending: 3,
    no_date: 4,
    completed: 5,
  };

  items.sort((a, b) => {
    const orderDiff = statusOrder[a.status] - statusOrder[b.status];
    if (orderDiff !== 0) return orderDiff;
    if (a.date && b.date) return a.date.getTime() - b.date.getTime();
    if (a.date) return -1;
    if (b.date) return 1;
    return 0;
  });

  return items;
}
